import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge, ListGroup, Form, Nav, ProgressBar, Dropdown, Modal } from 'react-bootstrap';
import { Plus, CheckSquare, Bullseye, Gear, ThreeDotsVertical, Filter, ChevronDown } from 'react-bootstrap-icons';
import CreateTaskModal from './CreateTaskModal';
import CreateGoalModal from './CreateGoalModal';
import CreateBoardModal from './CreateBoardModal';
import EisenhowerMatrix from './EisenhowerMatrix';
import KanbanBoard from './KanbanBoard';
import MoSCoWBoard from './MoSCoWBoard';
import TechniqueSettingsModal from './TechniqueSettingsModal';
import EditTaskModal from './EditTaskModal';
import TaskCompletionModal from './TaskCompletionModal';
import LabelsSettings from './LabelsSettings';
import BucketList from './BucketList';
import MedicalRecords from './MedicalRecords';
import Notes from './Notes';
import TimeTracking from './TimeTracking';
import { getDueDateStatus, getTaskBorderColor } from '../../shared/utils/taskColors';
import EditGoalModal from './EditGoalModal';
import PomodoroTimer from './PomodoroTimer';
import axios from 'axios';
import './LifeManagementDashboard.css';
import './Goals.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const LifeManagementDashboard = ({ activeProfileId }) => {
    const [tasks, setTasks] = useState([]);
    const [goals, setGoals] = useState([]);
    const [goalProgress, setGoalProgress] = useState({});
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
    const [showCreateGoalModal, setShowCreateGoalModal] = useState(false);
    const [showGoalProgressModal, setShowGoalProgressModal] = useState(false);
    const [showGoalProgressHistory, setShowGoalProgressHistory] = useState(false);
    const [activeTab, setActiveTab] = useState('tasks');
    const [showAllTasksView, setShowAllTasksView] = useState(false);
    const [showCompletedTasks, setShowCompletedTasks] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        status: 'all', // all, pending, completed
        createdDateFrom: '',
        createdDateTo: '',
        dueDateFrom: '',
        dueDateTo: '',
        completedDateFrom: '',
        completedDateTo: '',
        labels: [],
        priority: 'all' // all, urgent, high, medium, low
    });
    const [showTechniqueSettings, setShowTechniqueSettings] = useState(false);
    const [showCreateBoardModal, setShowCreateBoardModal] = useState(false);
    const [boards, setBoards] = useState([]);
    const [activeBoard, setActiveBoard] = useState(null);
    const [showEditTaskModal, setShowEditTaskModal] = useState(false);
    const [showTaskCompletionModal, setShowTaskCompletionModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showEditGoalModal, setShowEditGoalModal] = useState(false);
    const [selectedGoal, setSelectedGoal] = useState(null);
    const [goalProgressDate, setGoalProgressDate] = useState('');
    const [goalProgressNotes, setGoalProgressNotes] = useState('');
    const [goalNextSteps, setGoalNextSteps] = useState('');
    const [taskProgress, setTaskProgress] = useState({});
    const [showTaskProgressModal, setShowTaskProgressModal] = useState(false);
    const [showTaskProgressHistory, setShowTaskProgressHistory] = useState(false);
    const [taskProgressDate, setTaskProgressDate] = useState('');
    const [taskProgressNotes, setTaskProgressNotes] = useState('');
    const [taskNextSteps, setTaskNextSteps] = useState('');
    const [profileTechniques, setProfileTechniques] = useState(null);
    const [boardTechniques, setBoardTechniques] = useState(null);
    const [activeTimerTask, setActiveTimerTask] = useState(null);
    const [availableLabels, setAvailableLabels] = useState([]);

    useEffect(() => {
        if (activeProfileId) {
            // Clear existing tasks when switching profiles
            setTasks([]);
            setGoals([]);
            fetchTasks();
            fetchGoals();
            fetchProfileTechniques();
            fetchBoards();
            fetchLabels();
        }
    }, [activeProfileId]);

    // Update board techniques when active board changes
    useEffect(() => {
        if (activeBoard && activeProfileId) {
            fetchTasks(activeBoard.id);
            setBoardTechniques({
                primaryPrioritization: activeBoard.primary_prioritization,
                primaryTimeManagement: activeBoard.primary_time_management
            });
        }
    }, [activeBoard]);

    const fetchTaskProgress = async (taskId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/tasks/${taskId}/progress`);
            setTaskProgress(prev => ({ ...prev, [taskId]: response.data }));
        } catch (error) {
            console.error('Error fetching task progress:', error);
        }
    };

    const fetchTasks = async (boardId = null) => {
        try {
            let url = `${API_BASE_URL}/api/life/profiles/${activeProfileId}/tasks`;
            if (boardId) {
                url += `?board_id=${boardId}`;
            }
            const response = await axios.get(url);
            setTasks(response.data);
            
            // Fetch progress for each task
            for (const task of response.data) {
                fetchTaskProgress(task.id);
            }
        } catch (error) {
            console.error('Error fetching tasks:', error);
        }
    };

    const fetchGoals = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/goals`);
            setGoals(response.data);
            
            // Fetch progress for each goal
            for (const goal of response.data) {
                fetchGoalProgress(goal.id);
            }
        } catch (error) {
            console.error('Error fetching goals:', error);
        }
    };

    const fetchGoalProgress = async (goalId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/goals/${goalId}/progress`);
            setGoalProgress(prev => ({ ...prev, [goalId]: response.data }));
        } catch (error) {
            console.error('Error fetching goal progress:', error);
        }
    };

    const fetchProfileTechniques = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/profiles/${activeProfileId}`);
            const techniques = {
                primaryPrioritization: response.data.primary_prioritization,
                primaryTimeManagement: response.data.primary_time_management
            };
            setProfileTechniques(techniques);
        } catch (error) {
            console.error('Error fetching profile techniques:', error);
        }
    };

    const fetchBoards = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/boards`);
            setBoards(response.data);
            if (response.data.length > 0 && !activeBoard) {
                setActiveBoard(response.data[0]);
            }
        } catch (error) {
            console.error('Error fetching boards:', error);
            // Create default board if none exist
            if (error.response?.status === 404) {
                handleCreateBoard({ name: 'Default Board', description: 'Main task board' });
            }
        }
    };

    const fetchLabels = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/labels`);
            setAvailableLabels(response.data);
        } catch (error) {
            console.error('Error fetching labels:', error);
        }
    };

    const handleCreateBoard = async (boardData) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/boards`, boardData);
            const newBoard = response.data;
            setBoards([...boards, newBoard]);
            setActiveBoard(newBoard);
            setShowCreateBoardModal(false);
        } catch (error) {
            console.error('Error creating board:', error);
        }
    };

    const handleCreateTask = async (taskData) => {
        try {
            // Add board_id to task data if active board exists
            if (activeBoard) {
                taskData.board_id = activeBoard.id;
            }
            const response = await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/tasks`, taskData);
            setTasks([...tasks, response.data]);
        } catch (error) {
            console.error('Error creating task:', error);
        }
    };

    const handleCreateGoal = async (goalData) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/api/life/profiles/${activeProfileId}/goals`, goalData);
            setGoals([...goals, response.data]);
        } catch (error) {
            console.error('Error creating goal:', error);
        }
    };

    const handleToggleTaskStatus = async (taskId, currentStatus) => {
        if (currentStatus === 'TODO') {
            // Show completion modal for completing tasks
            const task = tasks.find(t => t.id === taskId);
            setSelectedTask(task);
            setShowTaskCompletionModal(true);
        } else {
            // Reopen completed task
            try {
                const response = await axios.put(`${API_BASE_URL}/api/life/tasks/${taskId}`, { status: 'TODO' });
                setTasks(tasks.map(task => task.id === taskId ? response.data : task));
            } catch (error) {
                console.error('Error updating task status:', error);
            }
        }
    };

    const handleCompleteTask = async (taskId, completionData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/api/life/tasks/${taskId}`, completionData);
            setTasks(tasks.map(task => task.id === taskId ? response.data : task));
        } catch (error) {
            console.error('Error completing task:', error);
        }
    };

    const handleQuickTaskUpdate = async (updatedTask) => {
        // Update the task in the local state immediately for responsiveness
        setTasks(prevTasks => prevTasks.map(task => task.id === updatedTask.id ? updatedTask : task));
        
        // Also refetch tasks to ensure we have the latest data with all label details
        if (activeBoard) {
            fetchTasks(activeBoard.id);
        }
    };

    const handleToggleGoalStatus = async (goalId, currentStatus) => {
        try {
            let newStatus;
            if (currentStatus === 'NOT_STARTED') {
                newStatus = 'IN_PROGRESS';
            } else if (currentStatus === 'IN_PROGRESS') {
                newStatus = 'COMPLETED';
            } else {
                newStatus = 'IN_PROGRESS';
            }
            const response = await axios.put(`${API_BASE_URL}/api/life/goals/${goalId}`, { status: newStatus });
            setGoals(goals.map(goal => goal.id === goalId ? response.data : goal));
        } catch (error) {
            console.error('Error updating goal status:', error);
        }
    };

    const handleUpdateTechniques = async (techniqueData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/api/life/boards/${activeBoard.id}/techniques`, techniqueData);
            
            // Update the active board with new techniques
            setActiveBoard(response.data);
            setBoardTechniques({
                primaryPrioritization: response.data.primary_prioritization,
                primaryTimeManagement: response.data.primary_time_management
            });
            
            // Update the board in the boards list
            setBoards(boards.map(board => board.id === activeBoard.id ? response.data : board));
        } catch (error) {
            console.error('Error updating board techniques:', error);
        }
    };

    const handleEditTask = (task) => {
        // Force refresh task data from server to get latest values
        fetchTaskById(task.id).then(refreshedTask => {
            setSelectedTask(refreshedTask || task);
            setShowEditTaskModal(true);
        });
    };

    const fetchTaskById = async (taskId) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/life/tasks/${taskId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching task:', error);
            return null;
        }
    };

    const handleUpdateTask = async (taskId, taskData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/api/life/tasks/${taskId}`, taskData);
            setTasks(prevTasks => prevTasks.map(task => task.id === taskId ? response.data : task));
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleUpdateTaskStatus = async (taskId, statusData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/api/life/tasks/${taskId}`, statusData);
            setTasks(tasks.map(task => task.id === taskId ? response.data : task));
        } catch (error) {
            console.error('Error updating task status:', error);
        }
    };

    const handleDeleteTask = async (taskId) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/life/tasks/${taskId}`);
            setTasks(tasks.filter(task => task.id !== taskId));
        } catch (error) {
            console.error('Error deleting task:', error);
        }
    };

    const handleEditGoal = (goal) => {
        setSelectedGoal(goal);
        setShowEditGoalModal(true);
    };

    const handleUpdateGoal = async (goalId, goalData) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/api/life/goals/${goalId}`, goalData);
            setGoals(goals.map(goal => goal.id === goalId ? response.data : goal));
        } catch (error) {
            console.error('Error updating goal:', error);
        }
    };

    const handleDeleteGoal = async (goalId) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/life/goals/${goalId}`);
            setGoals(goals.filter(goal => goal.id !== goalId));
        } catch (error) {
            console.error('Error deleting goal:', error);
        }
    };

    const handleAddGoalProgress = (goal) => {
        setSelectedGoal(goal);
        setGoalProgressDate(new Date().toISOString().split('T')[0]);
        setGoalProgressNotes('');
        setGoalNextSteps('');
        setShowGoalProgressModal(true);
    };

    const handleSubmitGoalProgress = async () => {
        try {
            await axios.post(`${API_BASE_URL}/api/life/goals/${selectedGoal.id}/progress`, {
                progress_date: goalProgressDate,
                notes: goalProgressNotes,
                next_steps: goalNextSteps
            });
            
            fetchGoalProgress(selectedGoal.id);
            
            setShowGoalProgressModal(false);
            setSelectedGoal(null);
            setGoalProgressDate('');
            setGoalProgressNotes('');
            setGoalNextSteps('');
        } catch (error) {
            console.error('Error adding goal progress:', error);
        }
    };

    const handleAddTaskProgress = (task) => {
        setSelectedTask(task);
        setTaskProgressDate(new Date().toISOString().split('T')[0]);
        setTaskProgressNotes('');
        setTaskNextSteps('');
        setShowTaskProgressModal(true);
    };

    const handleSubmitTaskProgress = async () => {
        try {
            const formData = new FormData();
            formData.append('progress_date', taskProgressDate);
            formData.append('notes', taskProgressNotes);
            if (taskNextSteps) formData.append('next_steps', taskNextSteps);

            await axios.post(`${API_BASE_URL}/api/life/tasks/${selectedTask.id}/progress`, formData);
            
            fetchTaskProgress(selectedTask.id);
            
            setShowTaskProgressModal(false);
            setSelectedTask(null);
            setTaskProgressDate('');
            setTaskProgressNotes('');
            setTaskNextSteps('');
        } catch (error) {
            console.error('Error adding task progress:', error);
        }
    };

    const handleStartPomodoro = (task) => {
        setActiveTimerTask(task);
    };

    const handleTimerComplete = (task, eventType) => {
        // Could track pomodoro sessions in database here
    };

    const applyFilters = (tasks) => {
        return tasks.filter(task => {
            // Status filter
            if (filters.status === 'pending' && task.status === 'COMPLETED') return false;
            if (filters.status === 'completed' && task.status !== 'COMPLETED') return false;
            
            // Created date filter
            if (filters.createdDateFrom) {
                const createdDate = new Date(task.created_at);
                const fromDate = new Date(filters.createdDateFrom);
                if (createdDate < fromDate) return false;
            }
            if (filters.createdDateTo) {
                const createdDate = new Date(task.created_at);
                const toDate = new Date(filters.createdDateTo);
                toDate.setHours(23, 59, 59, 999); // End of day
                if (createdDate > toDate) return false;
            }
            
            // Due date filter
            if (filters.dueDateFrom && task.due_date) {
                const dueDate = new Date(task.due_date);
                const fromDate = new Date(filters.dueDateFrom);
                if (dueDate < fromDate) return false;
            }
            if (filters.dueDateTo && task.due_date) {
                const dueDate = new Date(task.due_date);
                const toDate = new Date(filters.dueDateTo);
                toDate.setHours(23, 59, 59, 999);
                if (dueDate > toDate) return false;
            }
            
            // Completed date filter
            if (filters.completedDateFrom && task.completed_at) {
                const completedDate = new Date(task.completed_at);
                const fromDate = new Date(filters.completedDateFrom);
                if (completedDate < fromDate) return false;
            }
            if (filters.completedDateTo && task.completed_at) {
                const completedDate = new Date(task.completed_at);
                const toDate = new Date(filters.completedDateTo);
                toDate.setHours(23, 59, 59, 999);
                if (completedDate > toDate) return false;
            }
            
            // Labels filter
            if (filters.labels.length > 0) {
                const taskLabelIds = task.labels ? task.labels.map(label => label.id) : [];
                const hasMatchingLabel = filters.labels.some(labelId => taskLabelIds.includes(labelId));
                if (!hasMatchingLabel) return false;
            }
            
            // Priority filter
            if (filters.priority !== 'all') {
                const taskPriority = task.priority_level === 1 ? 'urgent' : 
                                   task.priority_level === 2 ? 'high' : 
                                   task.priority_level === 3 ? 'medium' : 'low';
                if (taskPriority !== filters.priority) return false;
            }
            
            return true;
        });
    };

    const clearFilters = () => {
        setFilters({
            status: 'all',
            createdDateFrom: '',
            createdDateTo: '',
            dueDateFrom: '',
            dueDateTo: '',
            completedDateFrom: '',
            completedDateTo: '',
            labels: [],
            priority: 'all'
        });
    };

    const filteredTasks = applyFilters(tasks);

    return (
        <div className="life-management-dashboard">
            <div className="dashboard-header">
                <h3>Life Management</h3>
            </div>

            <Nav variant="tabs" className="dashboard-tabs mb-4">
                <Nav.Item>
                    <Nav.Link active={activeTab === 'tasks'} onClick={() => setActiveTab('tasks')}>
                        Tasks
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'goals'} onClick={() => setActiveTab('goals')}>
                        Goals
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'bucket'} onClick={() => setActiveTab('bucket')}>
                        Bucket List
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'medical'} onClick={() => setActiveTab('medical')}>
                        Medical Records
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'notes'} onClick={() => setActiveTab('notes')}>
                        Notes
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'time-tracking'} onClick={() => setActiveTab('time-tracking')}>
                        Time Tracking
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link active={activeTab === 'labels'} onClick={() => setActiveTab('labels')}>
                        Labels
                    </Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'tasks' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h5><CheckSquare className="me-2" />Tasks</h5>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => setShowTechniqueSettings(true)}>
                                <Gear className="me-1" /> Technique Settings
                            </Button>
                            <Button variant="outline-info" size="sm" onClick={() => setShowCreateBoardModal(true)}>
                                <Plus /> New Board
                            </Button>
                            <Button variant="outline-success" size="sm" onClick={() => setShowAllTasksView(true)}>
                                All Tasks
                            </Button>
                            <Button variant="outline-primary" size="sm" onClick={() => setShowCreateTaskModal(true)}>
                                <Plus /> Add Task
                            </Button>
                        </div>
                    </div>
                    
                    {/* Board Selection */}
                    {boards.length > 0 && (
                        <div className="mb-3">
                            <Form.Select 
                                value={activeBoard?.id || ''} 
                                onChange={(e) => {
                                    const selectedBoard = boards.find(b => b.id === parseInt(e.target.value));
                                    setActiveBoard(selectedBoard);
                                }}
                                style={{ width: '300px' }}
                            >
                                {boards.map(board => (
                                    <option key={board.id} value={board.id}>
                                        {board.name} {board.description && `- ${board.description}`}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                    )}
                    {boardTechniques?.primaryTimeManagement === 'POMODORO' && (
                        <Row className="mb-4">
                            <Col md={4}>
                                <PomodoroTimer 
                                    activeTask={activeTimerTask}
                                    onTimerComplete={handleTimerComplete}
                                />
                            </Col>
                            <Col md={8}>
                                {/* Main content will be rendered below */}
                            </Col>
                        </Row>
                    )}
            
                    {boardTechniques?.primaryPrioritization === 'EISENHOWER' ? (
                        <Row>
                            <Col md={12}>
                                <EisenhowerMatrix 
                                    tasks={tasks.filter(t => t.status !== 'COMPLETED')} 
                                    onToggleTaskStatus={handleToggleTaskStatus}
                                    onEditTask={handleEditTask}
                                    onAddTaskProgress={handleAddTaskProgress}
                                    onStartPomodoro={boardTechniques?.primaryTimeManagement === 'POMODORO' ? handleStartPomodoro : undefined}
                                    onUpdateTaskStatus={handleUpdateTaskStatus}
                                    activeProfileId={activeProfileId}
                                    onTaskUpdate={handleQuickTaskUpdate}
                                    taskProgress={taskProgress}
                                    setSelectedTask={setSelectedTask}
                                    setShowTaskProgressHistory={setShowTaskProgressHistory}
                                />
                            </Col>
                        </Row>
                    ) : boardTechniques?.primaryPrioritization === 'KANBAN' ? (
                        <div>
                            <KanbanBoard 
                                tasks={tasks.filter(t => t.status !== 'COMPLETED')} 
                                onToggleTaskStatus={handleToggleTaskStatus}
                                onEditTask={handleEditTask}
                                onAddTaskProgress={handleAddTaskProgress}
                                onUpdateTaskStatus={handleUpdateTaskStatus}
                                onStartPomodoro={boardTechniques?.primaryTimeManagement === 'POMODORO' ? handleStartPomodoro : undefined}
                                activeProfileId={activeProfileId}
                                onTaskUpdate={handleQuickTaskUpdate}
                                taskProgress={taskProgress}
                                setSelectedTask={setSelectedTask}
                                setShowTaskProgressHistory={setShowTaskProgressHistory}
                            />
                        </div>
                    ) : boardTechniques?.primaryPrioritization === 'MOSCOW' ? (
                        <div>
                            <MoSCoWBoard 
                                tasks={tasks.filter(t => t.status !== 'COMPLETED')} 
                                onToggleTaskStatus={handleToggleTaskStatus}
                                onEditTask={handleEditTask}
                                onAddTaskProgress={handleAddTaskProgress}
                                onUpdateTaskStatus={handleUpdateTaskStatus}
                                onStartPomodoro={boardTechniques?.primaryTimeManagement === 'POMODORO' ? handleStartPomodoro : undefined}
                                activeProfileId={activeProfileId}
                                onTaskUpdate={handleQuickTaskUpdate}
                                taskProgress={taskProgress}
                                setSelectedTask={setSelectedTask}
                                setShowTaskProgressHistory={setShowTaskProgressHistory}
                                onTaskUpdate={handleQuickTaskUpdate}
                            />
                        </div>
                    ) : (
                        <Row>
                            <Col md={12}>
                                <div className="section-header">
                                    <h5 className="mb-3">
                                        <CheckSquare className="me-2" />
                                        Getting Things Done
                                        <Badge bg="light" text="dark" className="ms-2">
                                            {tasks.filter(t => t.status !== 'COMPLETED').length}/{tasks.length}
                                        </Badge>
                                    </h5>
                                </div>
                                {tasks.filter(t => t.status !== 'COMPLETED').length === 0 ? (
                                    <p className="text-muted">
                                        No pending tasks. Create your first task to get started!
                                    </p>
                                ) : (
                                    <ListGroup variant="flush" className="task-list-group">
                                        {tasks.filter(t => t.status !== 'COMPLETED').map(task => (
                                            <ListGroup.Item key={task.id} className={`d-flex justify-content-between align-items-center ${getTaskBorderColor(task)} border-start border-3`}>
                                                <div className="d-flex align-items-center">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={task.status === 'COMPLETED'}
                                                        onChange={() => handleToggleTaskStatus(task.id, task.status)}
                                                        className="me-3"
                                                    />
                                                    <div>
                                                        <div className="task-title">
                                                            {task.title}
                                                        </div>
                                                        {task.overview && (
                                                            <small className="text-muted d-block">{task.overview}</small>
                                                        )}
                                                        {task.due_date && (
                                                            <small className={`d-block ${getDueDateStatus(task.due_date) === 'overdue' ? 'text-danger' : getDueDateStatus(task.due_date) === 'due-soon' ? 'text-warning' : 'text-muted'}`}>
                                                                Due: {new Date(task.due_date).toLocaleDateString()}
                                                            </small>
                                                        )}
                                                        {task.labels && task.labels.length > 0 && (
                                                            <div className="mt-1">
                                                                {task.labels.map(label => (
                                                                    <Badge 
                                                                        key={label.id} 
                                                                        style={{backgroundColor: label.color, color: '#fff'}}
                                                                        className="me-1"
                                                                    >
                                                                        {label.name}
                                                                    </Badge>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {taskProgress[task.id] && taskProgress[task.id].length > 0 && (
                                                            <div className="mt-1">
                                                                <Badge 
                                                                    bg="info" 
                                                                    style={{cursor: 'pointer'}}
                                                                    onClick={() => {
                                                                        setSelectedTask(task);
                                                                        setShowTaskProgressHistory(true);
                                                                    }}
                                                                >
                                                                    📈 {taskProgress[task.id].length} Progress Entries
                                                                </Badge>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="d-flex flex-column align-items-end">
                                                    <Badge bg={task.priority_level === 1 ? 'danger' : task.priority_level === 2 ? 'warning' : 'secondary'} className="mb-1">
                                                        {task.priority_level === 1 ? 'URGENT' : task.priority_level === 2 ? 'HIGH' : task.priority_level === 3 ? 'MEDIUM' : 'LOW'}
                                                    </Badge>
                                                    <div className="d-flex gap-1">
                                                        <Button variant="link" size="sm" onClick={() => handleAddTaskProgress(task)} title="Log Progress">
                                                            📈
                                                        </Button>
                                                        <Button variant="link" size="sm" onClick={() => handleEditTask(task)}>
                                                            ✏️
                                                        </Button>
                                                    </div>
                                                </div>
                                            </ListGroup.Item>
                                        ))}
                                    </ListGroup>
                                )}
                            </Col>
                        </Row>
                    )}
                </div>
            )}



            {/* All Tasks Modal with Filtering */}
            <Modal show={showAllTasksView} onHide={() => setShowAllTasksView(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>All Tasks</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="section-header">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="me-3">
                                <CheckSquare className="me-2" />
                                Tasks 
                                <Badge bg="light" text="dark" className="ms-2">
                                    {filteredTasks.length}/{tasks.length}
                                </Badge>
                            </h6>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => setShowFilters(!showFilters)}
                                >
                                    <Filter size={14} className="me-1" />
                                    Filters
                                    <ChevronDown size={14} className="ms-1" />
                                </Button>
                            </div>
                        </div>
                        
                        {showFilters && (
                            <Card className="mb-3">
                                <Card.Body>
                                    <Row>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Status</Form.Label>
                                                <Form.Select
                                                    value={filters.status}
                                                    onChange={(e) => setFilters({...filters, status: e.target.value})}
                                                >
                                                    <option value="all">All Tasks</option>
                                                    <option value="pending">Pending Only</option>
                                                    <option value="completed">Completed Only</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={3}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Priority</Form.Label>
                                                <Form.Select
                                                    value={filters.priority}
                                                    onChange={(e) => setFilters({...filters, priority: e.target.value})}
                                                >
                                                    <option value="all">All Priorities</option>
                                                    <option value="urgent">Urgent</option>
                                                    <option value="high">High</option>
                                                    <option value="medium">Medium</option>
                                                    <option value="low">Low</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Labels</Form.Label>
                                                <Form.Select
                                                    multiple
                                                    value={filters.labels}
                                                    onChange={(e) => {
                                                        const selectedLabels = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                                        setFilters({...filters, labels: selectedLabels});
                                                    }}
                                                >
                                                    {availableLabels.map(label => (
                                                        <option key={label.id} value={label.id}>
                                                            {label.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Created From</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={filters.createdDateFrom}
                                                            onChange={(e) => setFilters({...filters, createdDateFrom: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Created To</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={filters.createdDateTo}
                                                            onChange={(e) => setFilters({...filters, createdDateTo: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                        <Col md={6}>
                                            <Row>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Due From</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={filters.dueDateFrom}
                                                            onChange={(e) => setFilters({...filters, dueDateFrom: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Due To</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={filters.dueDateTo}
                                                            onChange={(e) => setFilters({...filters, dueDateTo: e.target.value})}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    {filters.status === 'completed' || filters.status === 'all' ? (
                                        <Row>
                                            <Col md={6}>
                                                <Row>
                                                    <Col>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Completed From</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={filters.completedDateFrom}
                                                                onChange={(e) => setFilters({...filters, completedDateFrom: e.target.value})}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                    <Col>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Completed To</Form.Label>
                                                            <Form.Control
                                                                type="date"
                                                                value={filters.completedDateTo}
                                                                onChange={(e) => setFilters({...filters, completedDateTo: e.target.value})}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    ) : null}
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                            Clear All
                                        </Button>
                                        <Button variant="primary" size="sm" onClick={() => setShowFilters(false)}>
                                            Apply Filters
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                    {filteredTasks.length === 0 ? (
                        <p className="text-muted">
                            {tasks.length === 0 
                                ? 'No tasks yet. Create your first task to get started!' 
                                : 'No tasks match the current filters. Try adjusting your filter criteria.'
                            }
                        </p>
                    ) : (
                        <ListGroup variant="flush" className="task-list-group">
                            {filteredTasks.map(task => (
                                <ListGroup.Item key={task.id} className={`d-flex justify-content-between align-items-center ${getTaskBorderColor(task)} border-start border-3`}>
                                    <div className="d-flex align-items-center">
                                        <Form.Check
                                            type="checkbox"
                                            checked={task.status === 'COMPLETED'}
                                            onChange={() => handleToggleTaskStatus(task.id, task.status)}
                                            className="me-3"
                                        />
                                        <div>
                                            <div className={`task-title ${task.status === 'COMPLETED' ? 'text-decoration-line-through text-muted' : ''}`}>
                                                {task.title}
                                            </div>
                                            {task.overview && (
                                                <small className="text-muted d-block">{task.overview}</small>
                                            )}
                                            {task.due_date && (
                                                <small className={`d-block ${getDueDateStatus(task.due_date) === 'overdue' ? 'text-danger' : getDueDateStatus(task.due_date) === 'due-soon' ? 'text-warning' : 'text-muted'}`}>
                                                    Due: {new Date(task.due_date).toLocaleDateString()}
                                                </small>
                                            )}
                                            {task.labels && task.labels.length > 0 && (
                                                <div className="mt-1">
                                                    {task.labels.map(label => (
                                                        <Badge 
                                                            key={label.id} 
                                                            style={{backgroundColor: label.color, color: '#fff'}}
                                                            className="me-1"
                                                        >
                                                            {label.name}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="d-flex flex-column align-items-end">
                                        <Badge bg={task.priority_level === 1 ? 'danger' : task.priority_level === 2 ? 'warning' : 'secondary'} className="mb-1">
                                            {task.priority_level === 1 ? 'URGENT' : task.priority_level === 2 ? 'HIGH' : task.priority_level === 3 ? 'MEDIUM' : 'LOW'}
                                        </Badge>
                                        <Button variant="link" size="sm" onClick={() => handleEditTask(task)}>
                                            ✏️
                                        </Button>
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAllTasksView(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {activeTab === 'goals' && (
                <div className="dashboard-section">
                    <div className="section-header">
                        <h5><Bullseye className="me-2" />Goals</h5>
                        <Button variant="outline-success" size="sm" onClick={() => setShowCreateGoalModal(true)}>
                            <Plus /> Add Goal
                        </Button>
                    </div>
                    {goals.length === 0 ? (
                        <p className="text-muted">No goals yet. Set your first goal to start tracking progress!</p>
                    ) : (
                        <div className="goals-grid">
                            {goals.map(goal => (
                                <div key={goal.id} className="goal-card-enhanced">
                                    <div className="goal-card-header">
                                        <div className="goal-card-title">{goal.title}</div>
                                        <Dropdown align="end" className="goal-card-actions">
                                            <Dropdown.Toggle as="a" variant="link" className="p-0">
                                                <ThreeDotsVertical />
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => handleEditGoal(goal)}>Edit</Dropdown.Item>
                                                <Dropdown.Item onClick={() => handleDeleteGoal(goal.id)} className="text-danger">Delete</Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>
                                    <div className="goal-card-body">
                                        {goal.description && <div className="goal-card-description">{goal.description}</div>}
                                        {goal.target_date && <div className="goal-card-target-date">Target: {new Date(goal.target_date).toLocaleDateString()}</div>}
                                        {goalProgress[goal.id] && goalProgress[goal.id].length > 0 && (
                                            <div className="goal-progress-info">
                                                <Badge 
                                                    bg="info" 
                                                    style={{cursor: 'pointer'}}
                                                    onClick={() => {
                                                        setSelectedGoal(goal);
                                                        setShowGoalProgressHistory(true);
                                                    }}
                                                >
                                                    📈 {goalProgress[goal.id].length} Progress Entries
                                                </Badge>
                                            </div>
                                        )}
                                    </div>
                                    <div className="goal-card-footer">
                                        <div className="goal-card-progress w-100">
                                            <ProgressBar 
                                                now={goal.status === 'COMPLETED' ? 100 : goal.status === 'IN_PROGRESS' ? 50 : 0} 
                                                label={`${goal.status === 'COMPLETED' ? 100 : goal.status === 'IN_PROGRESS' ? 50 : 0}%`} 
                                                visuallyHidden 
                                            />
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <div className={`goal-card-status ${goal.status === 'COMPLETED' ? 'completed' : goal.status === 'IN_PROGRESS' ? 'in-progress' : 'not-started'}`}>
                                                {goal.status === 'COMPLETED' ? 'Completed' : goal.status === 'IN_PROGRESS' ? 'In Progress' : 'Not Started'}
                                            </div>
                                            <Button
                                                size="sm"
                                                variant="outline-info"
                                                onClick={() => handleAddGoalProgress(goal)}
                                                title="Log Progress"
                                            >
                                                📈
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant={goal.status === 'NOT_STARTED' ? 'success' : goal.status === 'IN_PROGRESS' ? 'primary' : 'secondary'}
                                                onClick={() => handleToggleGoalStatus(goal.id, goal.status)}
                                            >
                                                {goal.status === 'NOT_STARTED' ? 'Start' : goal.status === 'IN_PROGRESS' ? 'Complete' : 'Restart'}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'bucket' && (
                <div className="dashboard-section">
                    <BucketList activeProfileId={activeProfileId} />
                </div>
            )}

            {activeTab === 'medical' && (
                <div className="dashboard-section">
                    <MedicalRecords activeProfileId={activeProfileId} />
                </div>
            )}

            {activeTab === 'notes' && (
                <div className="dashboard-section">
                    <Notes activeProfileId={activeProfileId} />
                </div>
            )}

            {activeTab === 'time-tracking' && (
                <div className="dashboard-section">
                    <TimeTracking activeProfileId={activeProfileId} />
                </div>
            )}

            {activeTab === 'labels' && (
                <div className="dashboard-section">
                    <LabelsSettings activeProfileId={activeProfileId} />
                </div>
            )}
            
            <CreateTaskModal
                show={showCreateTaskModal}
                handleClose={() => setShowCreateTaskModal(false)}
                onCreateTask={handleCreateTask}
                profileTechniques={boardTechniques}
                activeProfileId={activeProfileId}
            />
            
            <CreateGoalModal
                show={showCreateGoalModal}
                handleClose={() => setShowCreateGoalModal(false)}
                onCreateGoal={handleCreateGoal}
            />
            
            <CreateBoardModal
                show={showCreateBoardModal}
                handleClose={() => setShowCreateBoardModal(false)}
                onCreateBoard={handleCreateBoard}
            />
            
            <TechniqueSettingsModal
                show={showTechniqueSettings}
                handleClose={() => setShowTechniqueSettings(false)}
                profileTechniques={boardTechniques}
                onUpdateTechniques={handleUpdateTechniques}
            />
            
            <EditTaskModal
                show={showEditTaskModal}
                handleClose={() => setShowEditTaskModal(false)}
                task={selectedTask}
                onUpdateTask={handleUpdateTask}
                onDeleteTask={handleDeleteTask}
                profileTechniques={boardTechniques}
                activeProfileId={activeProfileId}
            />
            
            <EditGoalModal
                show={showEditGoalModal}
                handleClose={() => setShowEditGoalModal(false)}
                goal={selectedGoal}
                onUpdateGoal={handleUpdateGoal}
                onDeleteGoal={handleDeleteGoal}
            />

            <TaskCompletionModal
                show={showTaskCompletionModal}
                handleClose={() => setShowTaskCompletionModal(false)}
                task={selectedTask}
                onCompleteTask={handleCompleteTask}
            />

            {/* Goal Progress Modal */}
            <Modal show={showGoalProgressModal} onHide={() => setShowGoalProgressModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Log Goal Progress</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedGoal && (
                        <>
                            <p><strong>{selectedGoal.title}</strong></p>
                            <Form.Group className="mb-3">
                                <Form.Label>Progress Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={goalProgressDate}
                                    onChange={(e) => setGoalProgressDate(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Progress Notes</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={goalProgressNotes}
                                    onChange={(e) => setGoalProgressNotes(e.target.value)}
                                    placeholder="What progress did you make? Any milestones achieved?"
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>Next Steps</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={goalNextSteps}
                                    onChange={(e) => setGoalNextSteps(e.target.value)}
                                    placeholder="What will you do next to move closer to this goal?"
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGoalProgressModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmitGoalProgress}>Log Progress</Button>
                </Modal.Footer>
            </Modal>

            {/* Goal Progress History Modal */}
            <Modal show={showGoalProgressHistory} onHide={() => setShowGoalProgressHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Goal Progress History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedGoal && (
                        <>
                            <h6>{selectedGoal.title}</h6>
                            <p className="text-muted mb-4">{selectedGoal.description}</p>
                            
                            {goalProgress[selectedGoal.id] && goalProgress[selectedGoal.id].length > 0 ? (
                                <div>
                                    <p><strong>Progress Entries ({goalProgress[selectedGoal.id].length}):</strong></p>
                                    {goalProgress[selectedGoal.id].map((entry, index) => (
                                        <div key={entry.id} className="mb-3 p-3 bg-light rounded">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <Badge bg="primary">Entry #{goalProgress[selectedGoal.id].length - index}</Badge>
                                                <small className="text-muted">
                                                    {new Date(entry.progress_date).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </small>
                                            </div>
                                            {entry.notes && (
                                                <div className="mb-2">
                                                    <strong>Progress:</strong>
                                                    <p className="mb-0 text-muted" style={{fontStyle: 'italic'}}>
                                                        "{entry.notes}"
                                                    </p>
                                                </div>
                                            )}
                                            {entry.next_steps && (
                                                <div className="mb-2">
                                                    <strong>Next Steps:</strong>
                                                    <p className="mb-0 text-success" style={{fontStyle: 'italic'}}>
                                                        "{entry.next_steps}"
                                                    </p>
                                                </div>
                                            )}
                                            {!entry.notes && !entry.next_steps && (
                                                <p className="mb-0 text-muted">
                                                    <em>No notes added</em>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted">No progress entries yet.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowGoalProgressHistory(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Task Progress Modal */}
            <Modal show={showTaskProgressModal} onHide={() => setShowTaskProgressModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Log Task Progress</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTask && (
                        <>
                            <h6>{selectedTask.title}</h6>
                            {selectedTask.overview && <p className="text-muted mb-4">{selectedTask.overview}</p>}
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Progress Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={taskProgressDate}
                                    onChange={(e) => setTaskProgressDate(e.target.value)}
                                />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Progress Notes *</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={taskProgressNotes}
                                    onChange={(e) => setTaskProgressNotes(e.target.value)}
                                    placeholder="What progress did you make on this task? Any milestones achieved?"
                                    required
                                />
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Label>Next Steps</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={taskNextSteps}
                                    onChange={(e) => setTaskNextSteps(e.target.value)}
                                    placeholder="What will you do next to continue progress on this task?"
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTaskProgressModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleSubmitTaskProgress}>Log Progress</Button>
                </Modal.Footer>
            </Modal>

            {/* Task Progress History Modal */}
            <Modal show={showTaskProgressHistory} onHide={() => setShowTaskProgressHistory(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Task Progress History</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTask && (
                        <>
                            <h6>{selectedTask.title}</h6>
                            {selectedTask.overview && <p className="text-muted mb-4">{selectedTask.overview}</p>}
                            
                            {taskProgress[selectedTask.id] && taskProgress[selectedTask.id].length > 0 ? (
                                <div>
                                    <p><strong>Progress Entries ({taskProgress[selectedTask.id].length}):</strong></p>
                                    {taskProgress[selectedTask.id].map((entry, index) => (
                                        <div key={entry.id} className="mb-3 p-3 bg-light rounded">
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <Badge bg="primary">Entry #{taskProgress[selectedTask.id].length - index}</Badge>
                                                <small className="text-muted">
                                                    {new Date(entry.progress_date).toLocaleDateString('en-US', {
                                                        weekday: 'long',
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })}
                                                </small>
                                            </div>
                                            {entry.notes && (
                                                <div className="mb-2">
                                                    <strong>Progress:</strong>
                                                    <p className="mb-0 text-muted" style={{fontStyle: 'italic'}}>
                                                        "{entry.notes}"
                                                    </p>
                                                </div>
                                            )}
                                            {entry.next_steps && (
                                                <div className="mb-2">
                                                    <strong>Next Steps:</strong>
                                                    <p className="mb-0 text-success" style={{fontStyle: 'italic'}}>
                                                        "{entry.next_steps}"
                                                    </p>
                                                </div>
                                            )}
                                            {!entry.notes && !entry.next_steps && (
                                                <p className="mb-0 text-muted">
                                                    <em>No notes added</em>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-muted">No progress entries yet.</p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTaskProgressHistory(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default LifeManagementDashboard;
